import { useEffect, useMemo, useState } from 'react';
import {
  getAdminProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImage,
} from '../services/adminApi';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import FormModal from '../components/FormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { PRODUCT_CATEGORIES, categoryLabel } from '../../utils/categories';

const PLANETS = [
  'Sun',
  'Moon',
  'Mars',
  'Mercury',
  'Jupiter',
  'Venus',
  'Saturn',
  'Rahu',
  'Ketu',
];

// Categories live in src/utils/categories.js so the admin form and the
// shop filter can never drift apart again.

const EMPTY_FORM = {
  product_code: '',
  name: '',
  description: '',
  planet: 'Sun',
  category: 'rings',
  price: '',
  original_price: '',
  stock_quantity: 10,
  image_url: '',
  tags: '',
  is_active: true,
  is_featured: false,
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product being edited, null = new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploading, setUploading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    const { data, error } = await getAdminProducts();
    setProducts(data);
    setLoadError(error);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  /* ---------- inventory grouping ----------
     Products are banded by category so stock can be checked a section
     at a time. Categories with nothing in them are still listed, so an
     empty one reads as "nothing added yet" rather than going unnoticed.
     Anything with a category not in the shared list lands in "Other" —
     otherwise a typo in the database would hide a product entirely. */
  const byCategory = useMemo(() => {
    const buckets = new Map(
      PRODUCT_CATEGORIES.map((c) => [c.value, { ...c, rows: [] }])
    );

    products.forEach((p) => {
      const key = p.category || 'uncategorised';
      if (!buckets.has(key)) {
        buckets.set(key, { value: key, label: categoryLabel(key), rows: [] });
      }
      buckets.get(key).rows.push(p);
    });

    return [...buckets.values()];
  }, [products]);

  const summarise = (rows) => {
    const units = rows.reduce((n, p) => n + Number(p.stock_quantity || 0), 0);
    const out = rows.filter((p) => Number(p.stock_quantity || 0) === 0).length;
    const low = rows.filter((p) => {
      const q = Number(p.stock_quantity || 0);
      return q > 0 && q <= 5;
    }).length;

    if (rows.length === 0) return 'nothing added yet';

    return [
      `${rows.length} product${rows.length === 1 ? '' : 's'}`,
      `${units} in stock`,
      low > 0 ? `${low} running low` : null,
      out > 0 ? `${out} out of stock` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  /* In the "All" view only populated bands are drawn — ten headings for
     one stocked category would bury it. The pills above still list every
     category with its count, so an empty one is visible there. */
  const shownGroups =
    categoryFilter === 'all'
      ? byCategory.filter((g) => g.rows.length > 0)
      : byCategory.filter((g) => g.value === categoryFilter);

  /* ---------- form helpers ---------- */

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const openAdd = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      product_code: `CS-${String(products.length + 1).padStart(3, '0')}`,
    });
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      product_code: p.product_code || '',
      name: p.name || '',
      description: p.description || '',
      planet: p.planet || 'Sun',
      category: p.category || 'rings',
      price: p.price ?? '',
      original_price: p.original_price ?? '',
      stock_quantity: p.stock_quantity ?? 0,
      image_url: p.image_url || '',
      tags: (p.tags || []).join(', '),
      is_active: p.is_active ?? true,
      is_featured: p.is_featured ?? false,
    });
    setFormError('');
    setModalOpen(true);
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setFormError('');
    const { url, error } = await uploadProductImage(file);
    setUploading(false);
    if (error) {
      setFormError(
        `Image upload failed: ${error.message}. ` +
          `Make sure a public "product-images" storage bucket exists in Supabase, ` +
          `or paste an image URL instead.`
      );
      return;
    }
    set('image_url', url);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    const price = Number(form.price);
    const originalPrice = form.original_price ? Number(form.original_price) : null;
    const stock = Number(form.stock_quantity);

    if (!form.name.trim()) return setFormError('Product name is required.');
    if (!price || price <= 0) return setFormError('Enter a valid price.');
    if (originalPrice && originalPrice < price)
      return setFormError('Original price should be higher than selling price.');
    if (Number.isNaN(stock) || stock < 0)
      return setFormError('Enter a valid stock quantity.');

    const payload = {
      product_code: form.product_code.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      planet: form.planet,
      category: form.category,
      price,
      original_price: originalPrice,
      discount_percent: originalPrice
        ? Math.round(((originalPrice - price) / originalPrice) * 100)
        : 0,
      stock_quantity: stock,
      in_stock: stock > 0,
      image_url: form.image_url.trim() || null,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      is_active: form.is_active,
      is_featured: form.is_featured,
    };

    setSaving(true);
    const { error } = editing
      ? await updateProduct(editing.id, payload)
      : await createProduct(payload);
    setSaving(false);

    if (error) {
      setFormError(error.message);
      return;
    }

    setModalOpen(false);
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await deleteProduct(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    if (error) {
      alert(
        `Could not delete: ${error.message}\n\nTip: if this product has orders, ` +
          `mark it Inactive instead of deleting.`
      );
      return;
    }
    load();
  };

  const toggleActive = async (p) => {
    await updateProduct(p.id, { is_active: !p.is_active });
    load();
  };

  const stockLabel = (p) => {
    const q = Number(p.stock_quantity);
    if (q === 0) return 'Out of Stock';
    if (q <= 3) return 'Low Stock';
    return 'In Stock';
  };

  /* ---------- render ---------- */

  return (
    <div>
      <div className="page-head">
        <h1>Products</h1>
        <button className="btn-primary" onClick={openAdd}>
          + Add Product
        </button>
      </div>

      {loadError && (
        <p className="admin-error">Failed to load products: {loadError.message}</p>
      )}

      <div className="filter-pills">
        <button
          className={`pill ${categoryFilter === 'all' ? 'active' : ''}`}
          onClick={() => setCategoryFilter('all')}
        >
          All ({products.length})
        </button>
        {byCategory.map((g) => (
          <button
            key={g.value}
            className={`pill ${categoryFilter === g.value ? 'active' : ''}`}
            onClick={() => setCategoryFilter(g.value)}
          >
            {g.label} ({g.rows.length})
          </button>
        ))}
      </div>

      <DataTable
        loading={loading}
        emptyMessage={
          categoryFilter === 'all'
            ? 'No products yet. Click “+ Add Product” to create your first one.'
            : `Nothing in ${categoryLabel(categoryFilter)} yet. Click “+ Add Product” and pick that category.`
        }
        groups={shownGroups.map((g) => ({
          key: g.value,
          label: g.label,
          meta: summarise(g.rows),
          rows: g.rows,
        }))}
        columns={[
          {
            key: 'image_url',
            label: '',
            width: '64px',
            render: (p) =>
              p.image_url ? (
                <img src={p.image_url} alt={p.name} className="thumb" />
              ) : (
                <div className="thumb thumb-empty">💎</div>
              ),
          },
          {
            key: 'name',
            label: 'Product',
            render: (p) => (
              <div>
                <div className="cell-title">{p.name}</div>
                <div className="cell-sub">
                  {p.product_code} · {p.planet} · {categoryLabel(p.category)}
                </div>
              </div>
            ),
          },
          {
            key: 'price',
            label: 'Price',
            render: (p) => (
              <div>
                <div className="cell-title">{inr(p.price)}</div>
                {p.original_price && (
                  <div className="cell-sub strike">{inr(p.original_price)}</div>
                )}
              </div>
            ),
          },
          {
            key: 'stock_quantity',
            label: 'Stock',
            render: (p) => (
              <div className="stock-cell">
                <span>{p.stock_quantity}</span>
                <StatusBadge status={stockLabel(p)} />
              </div>
            ),
          },
          {
            key: 'is_active',
            label: 'Visible',
            render: (p) => (
              <button
                className={`toggle ${p.is_active ? 'on' : ''}`}
                onClick={() => toggleActive(p)}
                title={p.is_active ? 'Shown in shop — click to hide' : 'Hidden — click to show'}
              >
                <span className="knob" />
              </button>
            ),
          },
          {
            key: 'actions',
            label: 'Actions',
            width: '140px',
            render: (p) => (
              <div className="action-buttons">
                <button className="btn-edit" onClick={() => openEdit(p)}>
                  Edit
                </button>
                <button className="btn-delete" onClick={() => setDeleteTarget(p)}>
                  Delete
                </button>
              </div>
            ),
          },
        ]}
      />

      {/* ADD / EDIT MODAL */}
      <FormModal
        open={modalOpen}
        wide
        title={editing ? `Edit — ${editing.name}` : 'Add Product'}
        onClose={() => setModalOpen(false)}
      >
        <form onSubmit={handleSave} className="admin-form">
          <div className="form-grid">
            <label>
              Product Name *
              <input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. Natural Ruby Ring"
              />
            </label>
            <label>
              Product Code
              <input
                value={form.product_code}
                onChange={(e) => set('product_code', e.target.value)}
                placeholder="CS-011"
              />
            </label>
            <label>
              Planet
              <select
                value={form.planet}
                onChange={(e) => set('planet', e.target.value)}
              >
                {PLANETS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
            <label>
              Category
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Selling Price (₹) *
              <input
                type="number"
                min="0"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
              />
            </label>
            <label>
              Original Price (₹, for discount display)
              <input
                type="number"
                min="0"
                value={form.original_price}
                onChange={(e) => set('original_price', e.target.value)}
              />
            </label>
            <label>
              Stock Quantity *
              <input
                type="number"
                min="0"
                value={form.stock_quantity}
                onChange={(e) => set('stock_quantity', e.target.value)}
              />
            </label>
            <label>
              Tags (comma separated)
              <input
                value={form.tags}
                onChange={(e) => set('tags', e.target.value)}
                placeholder="Bestseller, Premium, New"
              />
            </label>
          </div>

          <label>
            Description
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Certified natural ruby ring to enhance confidence…"
            />
          </label>

          <div className="image-field">
            <label>
              Product Image
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageFile(e.target.files?.[0])}
              />
            </label>
            <label>
              …or paste image URL
              <input
                value={form.image_url}
                onChange={(e) => set('image_url', e.target.value)}
                placeholder="https://… or /img/ruby-ring.jpg"
              />
            </label>
            {uploading && <p className="hint">Uploading image…</p>}
            {form.image_url && (
              <img src={form.image_url} alt="preview" className="image-preview" />
            )}
          </div>

          <div className="check-row">
            <label className="check">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              Visible in shop
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => set('is_featured', e.target.checked)}
              />
              Featured
            </label>
          </div>

          {formError && <p className="admin-error">{formError}</p>}

          <div className="admin-modal-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving || uploading}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Product'}
            </button>
          </div>
        </form>
      </FormModal>

      {/* DELETE CONFIRM */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete product?"
        message={`"${deleteTarget?.name}" will be permanently removed from the store.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
